import { createFileRoute, Link, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import {
  Home,
  Users,
  CreditCard,
  LineChart,
  Brain,
  FileText,
  Send,
  ShieldAlert,
  ShieldCheck,
  Settings as Cog,
  Shield,
  LogOut,
  Search,
  LayoutDashboard,

} from "lucide-react";
import { getMyRoles } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  ssr: false,
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return { isAdmin: false, adminEmail: null };
    const { roles } = await getMyRoles();
    if (!roles.includes("admin")) throw redirect({ to: "/dashboard" });
    return { isAdmin: true, adminEmail: context.user?.email ?? null };
  },
  component: AdminLayout,
});

const nav: Array<{ to: string; label: string; icon: typeof Home; exact?: boolean }> = [
  { to: "/dashboard", label: "User Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin", label: "Overview", icon: Home, exact: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { to: "/admin/market", label: "Market Control", icon: LineChart },
  { to: "/admin/signals", label: "Signal Intelligence", icon: Brain },
  { to: "/admin/content", label: "Content & FAQ", icon: FileText },
  { to: "/admin/telegram", label: "Telegram", icon: Send },
  { to: "/admin/security", label: "Security & Logs", icon: ShieldAlert },
  { to: "/admin/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/admin/seo", label: "SEO Monitoring", icon: Search },
  { to: "/admin/settings", label: "Settings", icon: Cog },
];

function AdminLayout() {
  const { adminEmail } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] w-full bg-background">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border/60 bg-card/40 backdrop-blur lg:flex">
        <div className="flex items-center gap-2 border-b border-border/60 px-4 py-4">
          <div className="rounded-md border border-primary/40 bg-primary/10 p-1.5 text-primary">
            <Shield className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-tight">Super Admin</div>
            <div className="text-[10px] text-muted-foreground">ForexMind console</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {nav.map((item) => {
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to as "/admin"}
                className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-xs font-medium transition-colors ${
                  active
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border/60 bg-background/80 px-5 py-3 backdrop-blur">
          <div className="lg:hidden">
            <h1 className="text-sm font-semibold tracking-tight">Super Admin</h1>
          </div>
          <div className="hidden text-xs text-muted-foreground lg:block">
            {nav.find((n) => (n.exact ? pathname === n.to : pathname.startsWith(n.to)))?.label ?? ""}
          </div>
          <div className="flex items-center gap-3 text-xs">
            {adminEmail && (
              <span className="hidden font-mono text-muted-foreground sm:inline">{adminEmail}</span>
            )}
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1.5 font-medium text-foreground transition-colors hover:bg-muted/40"
            >
              <LogOut className="h-3.5 w-3.5" />
              Exit Admin
            </Link>
          </div>
        </header>

        {/* Mobile top nav */}
        <div className="flex gap-1 overflow-x-auto border-b border-border/60 bg-card/30 px-2 py-1.5 lg:hidden">
          {nav.map((item) => {
            const active = item.exact ? pathname === item.to : pathname.startsWith(item.to);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to as "/admin"}
                className={`flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                  active
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                }`}
              >
                <Icon className="h-3 w-3" />
                {item.label}
              </Link>
            );
          })}
        </div>

        <main className="flex-1 p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
